import axios from 'axios';
import { asset } from '@/helpers';

export function use3DSAuth() {
    let scriptLoaded = false;
    let scriptLoading = false;

    /**
     * Carrega o script 3DS dinamicamente
     */
    const loadScript = (environment) => {
        return new Promise((resolve, reject) => {
            // Se já carregou, resolve imediatamente
            if (scriptLoaded && typeof window.bpmpi_authenticate === 'function') {
                resolve();
                return;
            }

            // Se está carregando, aguarda
            if (scriptLoading) {
                const checkInterval = setInterval(() => {
                    if (typeof window.bpmpi_authenticate === 'function') {
                        clearInterval(checkInterval);
                        resolve();
                    }
                }, 100);
                return;
            }

            scriptLoading = true;

            const scriptUrl = environment === 'PRD'
                ? 'https://mpi.braspag.com.br/Scripts/BP.Mpi.3ds20.min.js'
                : asset('scripts/3ds.c/sandbox.min.js');

            console.log('[3DS] Loading script from:', scriptUrl);

            const script = document.createElement('script');
            script.src = scriptUrl;
            script.type = 'text/javascript';
            script.id = 'cielo-3ds-script';

            script.onload = () => {
                console.log('[3DS] Script loaded successfully');
                scriptLoaded = true;
                scriptLoading = false;
                resolve();
            };

            script.onerror = () => {
                scriptLoading = false;
                reject(new Error('Falha ao carregar script 3DS'));
            };

            document.head.appendChild(script);
        });
    };

    /**
     * Autentica transação com 3DS 2.0 da Cielo/Braspag
     *
     * @param {Object} orderData - Dados do pedido
     * @param {Object} cardData - Dados do cartão
     * @param {Object} customerData - Dados do cliente
     * @param {Object} billingAddress - Endereço de cobrança (opcional)
     * @returns {Promise<Object>} - Resultado da autenticação {Cavv, Xid, Eci, Version, ReferenceId}
     */
    const authenticate3DS = async (orderData, cardData, customerData, billingAddress = null, visit = null) => {
        return new Promise(async (resolve, reject) => {
            try {
                // 1. Buscar access token do backend
                console.log('[3DS] Fetching access token...');
                const tokenResponse = await axios.post(route('internal_api.cielo.3ds-token'));
                const { access_token, config: cieloConfig } = tokenResponse.data;

                console.log('[3DS] Token received, environment:', cieloConfig.environment);

                // 2. Popular campos hidden ANTES de configurar bpmpi_config e carregar o script
                // Isso é CRÍTICO - o script da Braspag lê os campos ao inicializar
                populateHiddenFields(access_token, orderData, cardData, customerData, billingAddress, visit, cieloConfig.environment);

                // 3. Configurar bpmpi_config ANTES de carregar o script
                console.log('[3DS] Setting up bpmpi_config...');
                window.bpmpi_config = function() {
                    console.log('[3DS] bpmpi_config called by script');
                    return {
                        onReady: function() {
                            console.log('[3DS] onReady callback triggered');
                            showAuthModal();

                            // Chamar autenticação após um pequeno delay para garantir que os campos foram processados
                            setTimeout(() => {
                                if (typeof window.bpmpi_authenticate === 'function') {
                                    console.log('[3DS] Calling bpmpi_authenticate...');
                                    window.bpmpi_authenticate();
                                } else {
                                    hideAuthModal();
                                    reject(new Error('Função bpmpi_authenticate não disponível.'));
                                }
                            }, 100);
                        },
                        onSuccess: function(e) {
                            console.log('[3DS] Authentication successful', e);
                            hideAuthModal();
                            resolve({
                                Cavv: e.Cavv,
                                Xid: e.Xid,
                                Eci: e.Eci,
                                Version: e.Version,
                                ReferenceId: e.ReferenceId
                            });
                        },
                        onFailure: function(e) {
                            console.error('[3DS] Authentication failed', e);
                            hideAuthModal();
                            reject(new Error('Autenticação 3DS falhou. Por favor, tente novamente ou use outro cartão.'));
                        },
                        onUnenrolled: function(e) {
                            console.warn('[3DS] Card not enrolled for 3DS', e);
                            hideAuthModal();
                            // Continuar sem 3DS - cartão não está inscrito no programa
                            resolve({
                                Cavv: '',
                                Xid: e.Xid || '',
                                Eci: e.Eci || '',
                                Version: e.Version || '',
                                ReferenceId: e.ReferenceId || ''
                            });
                        },
                        onDisabled: function() {
                            console.log('[3DS] Authentication disabled');
                            hideAuthModal();
                            resolve({
                                Cavv: '',
                                Xid: '',
                                Eci: '',
                                Version: '',
                                ReferenceId: ''
                            });
                        },
                        onError: function(e) {
                            console.error('[3DS] Error during authentication', e);
                            hideAuthModal();
                            reject(new Error(`Erro técnico na autenticação 3DS: ${e.ReturnMessage || 'Erro desconhecido'}`));
                        },
                        onUnsupportedBrand: function(e) {
                            console.warn('[3DS] Unsupported card brand', e);
                            hideAuthModal();
                            reject(new Error('Bandeira do cartão não suportada para autenticação 3DS.'));
                        },
                        Environment: cieloConfig.environment,
                        Debug: cieloConfig.environment === 'SDB'
                    };
                };

                // 4. Remover script anterior se existir (para forçar reinicialização)
                const existingScript = document.getElementById('cielo-3ds-script');
                if (existingScript) {
                    existingScript.remove();
                    scriptLoaded = false;
                    scriptLoading = false;
                }

                // 5. Carregar script dinamicamente
                await loadScript(cieloConfig.environment);

            } catch (error) {
                hideAuthModal();
                console.error('[3DS] Error initializing authentication', error);
                reject(error);
            }
        });
    };

    /**
     * Popula campos hidden necessários para autenticação 3DS
     */
    const populateHiddenFields = (accessToken, orderData, cardData, customerData, billingAddress, visit, environment = 'SDB') => {
        console.log('[3DS] Populating hidden fields...');
        console.log('[3DS] Access Token received:', accessToken ? accessToken.substring(0, 50) + '...' : 'NULL/UNDEFINED');

        // Limpar campos existentes
        removeExistingFields();

        const container = document.body;

        // Access token - CRÍTICO
        if (!accessToken) {
            console.error('[3DS] ERROR: Access token is null or undefined!');
        }
        appendHiddenField(container, 'bpmpi_accesstoken', accessToken);

        // Autenticação habilitada
        appendHiddenField(container, 'bpmpi_auth', true);
        appendHiddenField(container, 'bpmpi_auth_notifyonly', false);
        appendHiddenField(container, 'bpmpi_auth_suppresschallenge', false);
        

        // Dados do pedido
        appendHiddenField(container, 'bpmpi_ordernumber', orderData.orderId.toString());
        appendHiddenField(container, 'bpmpi_currency', '986'); // 986 = brl
        appendHiddenField(container, 'bpmpi_totalamount', orderData.amount.toString());
        appendHiddenField(container, 'bpmpi_installments', (orderData.installments || 1).toString());
        appendHiddenField(container, 'bpmpi_paymentmethod', 'credit');

        // Dados do cartão
        appendHiddenField(container, 'bpmpi_cardnumber', cardData.number.replace(/\s/g, ''));
        appendHiddenField(container, 'bpmpi_cardexpirationmonth', cardData.expiration.split('/')[0]);
        appendHiddenField(container, 'bpmpi_cardexpirationyear', '20' + cardData.expiration.split('/')[1]);

        // Dados do cliente (billing)
        if (customerData) {
            appendHiddenField(container, 'bpmpi_billto_customerid', cardData.document.replace(/\D/g, '') || '');
            appendHiddenField(container, 'bpmpi_billto_name', customerData.name || '');
            appendHiddenField(container, 'bpmpi_billto_phonenumber', (customerData.phone || '').replace(/\D/g, ''));
            appendHiddenField(container, 'bpmpi_billto_email', customerData.email || '');
        }

        const defaultAddress = {
            street: 'Rua Pioneiro Yldefonso Nogueira de Campos, 926',
            neighborhood: 'Loteamento Bela Vista',
            city: 'Maringa',
            state: 'PR',
            zipCode: '87050685',
            country: 'BR'
        };

        const hasBillingAddress = billingAddress && billingAddress.city && billingAddress.state && billingAddress.zipCode && billingAddress.country;

        const street1 = hasBillingAddress ? (billingAddress.street || 'n/a') + (billingAddress?.number ? ', ' + billingAddress?.number : '') : defaultAddress.street;
        const street2 = hasBillingAddress ? (billingAddress?.complement ? billingAddress?.complement + ' - ' : '') + (billingAddress?.neighborhood ? billingAddress?.neighborhood : '') : defaultAddress.neighborhood;
        const city = hasBillingAddress ? billingAddress.city : defaultAddress.city;

        const countryCode = hasBillingAddress
            ? (billingAddress.country.length > 2 ? billingAddress.country.substring(0, 2).toUpperCase() : billingAddress.country.toUpperCase())
            : defaultAddress.country;
        const isBrazilUsaOrCanada = ['BR', 'US', 'CA'].includes(countryCode);
        const state = hasBillingAddress
            ? (isBrazilUsaOrCanada ? billingAddress.state : countryCode)
            : defaultAddress.state;
        
        const zipCode = hasBillingAddress ? billingAddress.zipCode : defaultAddress.zipCode;

        appendHiddenField(container, 'bpmpi_billto_street1', street1);
        appendHiddenField(container, 'bpmpi_billto_street2', street2);
        appendHiddenField(container, 'bpmpi_billto_city', city);
        appendHiddenField(container, 'bpmpi_billto_state', state);
        appendHiddenField(container, 'bpmpi_billto_zipcode', zipCode);
        appendHiddenField(container, 'bpmpi_billto_country', countryCode);

        appendHiddenField(container, 'bpmpi_shipto_sameasbillto', true);
        appendHiddenField(container, 'bpmpi_shipTo_shippingmethod', 'none');

        appendHiddenField(container, 'bpmpi_useraccount_guest', true);
        appendHiddenField(container, 'bpmpi_merchant_newcustomer', true); // TODO: verificar se ja existe compra do usuario ou nao
        
        const items = orderData.items || [];

        items.forEach((item, index) => {
            const n = index + 1;
            appendHiddenField(container, `bpmpi_cart_${n}_name`, item.name || '');
            appendHiddenField(container, `bpmpi_cart_${n}_description`, item.description || item.name || '');
            appendHiddenField(container, `bpmpi_cart_${n}_sku`, item.id || '');
            appendHiddenField(container, `bpmpi_cart_${n}_quantity`, String(item.quantity ?? 1));
            appendHiddenField(container, `bpmpi_cart_${n}_unitprice`, String(item.unit_price ?? 0));
        });

        appendHiddenField(container, 'bpmpi_transaction_mode', 'S'); // e-commerce
        

        appendHiddenField(container, 'bpmpi_device_ipaddress', visit?.ip ||'127.0.0.1');
        appendHiddenField(container, 'bpmpi_device_channel', 'Browser');

        // Merchant data (em ambiente não-prod, usar https://wwww.examplo.com.br)
        const merchantUrl = environment === 'PRD' ? window.location.origin : 'https://www.exemplo.com.br';
        appendHiddenField(container, 'bpmpi_merchant_url', merchantUrl);

        // TODO: bpmpi_brand_establishment_code implementar isso quando for AMEX
        //referencia: https://docs.cielo.com.br/ecommerce-cielo/docs/mapeando-classes

        // Marketing source (utm_source) para análise de risco 3DS
        const utmSource = visit?.query_params?.utm_source;
        if (utmSource) {
            appendHiddenField(container, 'bpmpi_order_marketingsource', utmSource);
        }

        appendHiddenField(container, 'bpmpi_order_marketingoptin', true);

        appendHiddenField(container, 'bpmpi_order_productcode', 'PHY'); // compra de mercadorias

        appendHiddenField(container, 'bpmpi_order_recurrence', false); // TODO: marcar como true quando for recorrencia

        // TODO: tambem adicionar: bpmpi_recurring_enddate, bpmpi_recurring_frequency, bpmpi_recurring_originalpurchasedate, bpmpi_order_recurrence

        

        // TODO: bpmpi_order_cardattemptslast24hours
        // TODO: bpmpi_order_countlast1year
        // TODO: bpmpi_order_countlast6months
        // TODO: bpmpi_order_countlast24hours
        
    };

    /**
     * Adiciona campo hidden ao DOM
     */
    const appendHiddenField = (container, className, value) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.className = className;
        input.value = value || '';
        input.setAttribute('data-3ds-field', 'true');
        container.appendChild(input);
    };

    /**
     * Remove campos hidden anteriores
     */
    const removeExistingFields = () => {
        const existingFields = document.querySelectorAll('[data-3ds-field="true"]');
        existingFields.forEach(field => field.remove());
    };

    /**
     * Mostra modal de autenticação 3DS
     */
    const showAuthModal = () => {
        const modal = document.getElementById('bpmpi-authentication-container');
        if (modal) {
            modal.classList.remove('hidden');
        }
    };

    /**
     * Esconde modal de autenticação 3DS
     */
    const hideAuthModal = () => {
        const modal = document.getElementById('bpmpi-authentication-container');
        if (modal) {
            modal.classList.add('hidden');
        }
    };

    return {
        authenticate3DS
    };
}
